/**
 * Opens/closes the Custom Order <dialog> on the PDP.
 *
 * Markup hooks (queried by data-attribute):
 *   [data-relic-custom-order-modal-open]  — trigger button (aria-controls → dialog id)
 *   [data-relic-custom-order-modal]       — <dialog> element
 *   [data-relic-custom-order-modal-close] — close button inside dialog
 *   [data-relic-product-name]             — product title to prefill the form field
 *   [data-relic-product-field]            — Shopify Forms field name (e.g. custom#product_name)
 *   [data-relic-custom-order-app]         — Shopify Forms app embed container
 */

const PRODUCT_FIELD_POPULATE_TIMEOUT_MS = 5000;
const CUSTOM_ORDER_APP_SELECTOR = '[data-relic-custom-order-app]';

function findInShadowTree(root, selector) {
  if (!root) return null;

  const directMatch = root.querySelector(selector);
  if (directMatch) return directMatch;

  for (const element of root.querySelectorAll('*')) {
    if (!element.shadowRoot) continue;

    const nestedMatch = findInShadowTree(element.shadowRoot, selector);
    if (nestedMatch) return nestedMatch;
  }

  return null;
}

function findProductNameField(dialog, fieldName) {
  const appRoot = dialog.querySelector(CUSTOM_ORDER_APP_SELECTOR);
  if (!appRoot) return null;

  const selectors = [
    `[name="${fieldName}"]`,
    `[id="${fieldName}"]`,
    `[data-testid="field-${fieldName}"]`,
  ];

  for (const selector of selectors) {
    const field = findInShadowTree(appRoot, selector);
    if (field) return field;
  }

  return null;
}

function setFieldValue(field, value) {
  if (!field || field.value === value) return true;

  field.value = value;
  field.dispatchEvent(
    new Event('input', {
      bubbles: true,
      cancelable: true,
    })
  );
  field.dispatchEvent(
    new Event('change', {
      bubbles: true,
      cancelable: true,
    })
  );

  return field.value === value;
}

function populateProductNameField(dialog) {
  const productName = dialog.dataset.relicProductName;
  const fieldName = dialog.dataset.relicProductField;

  if (!productName || !fieldName) return;

  const tryPopulate = () => {
    const field = findProductNameField(dialog, fieldName);
    if (!field) return false;
    return setFieldValue(field, productName);
  };

  if (tryPopulate()) return;

  if (dialog.__relicProductFieldObserver) {
    dialog.__relicProductFieldObserver.disconnect();
    dialog.__relicProductFieldObserver = null;
  }

  const appRoot = dialog.querySelector(CUSTOM_ORDER_APP_SELECTOR);
  if (!appRoot) return;

  const startedAt = Date.now();

  const observer = new MutationObserver(() => {
    if (tryPopulate()) {
      observer.disconnect();
      dialog.__relicProductFieldObserver = null;
      return;
    }

    if (Date.now() - startedAt > PRODUCT_FIELD_POPULATE_TIMEOUT_MS) {
      observer.disconnect();
      dialog.__relicProductFieldObserver = null;
    }
  });

  dialog.__relicProductFieldObserver = observer;
  observer.observe(appRoot, {
    childList: true,
    subtree: true,
  });
}

function bindCustomOrderModal(dialog) {
  if (dialog.dataset.relicCustomOrderModalBackdropInit) return;
  dialog.dataset.relicCustomOrderModalBackdropInit = 'true';

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) {
      dialog.close();
    }
  });

  dialog.addEventListener('toggle', () => {
    if (dialog.open) {
      populateProductNameField(dialog);
    }
  });

  populateProductNameField(dialog);
}

function initRelicCustomOrderModals() {
  if (!window.__relicCustomOrderModalsClickInit) {
    window.__relicCustomOrderModalsClickInit = true;

    document.addEventListener('click', (event) => {
      const openTrigger = event.target.closest('[data-relic-custom-order-modal-open]');
      if (openTrigger) {
        const dialogId = openTrigger.getAttribute('aria-controls');
        const dialog = dialogId ? document.getElementById(dialogId) : null;

        if (dialog?.matches('[data-relic-custom-order-modal]')) {
          event.preventDefault();
          dialog.showModal();
          populateProductNameField(dialog);
        }
        return;
      }

      const closeTrigger = event.target.closest('[data-relic-custom-order-modal-close]');
      if (closeTrigger) {
        const dialog = closeTrigger.closest('[data-relic-custom-order-modal]');
        dialog?.close();
      }
    });
  }

  document.querySelectorAll('[data-relic-custom-order-modal]').forEach(bindCustomOrderModal);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRelicCustomOrderModals);
} else {
  initRelicCustomOrderModals();
}

document.addEventListener('shopify:section:load', initRelicCustomOrderModals);
